import { Platform } from 'react-native';

/**
 * Web: structural `region` + `aria-labelledby` for landmarks, without RN’s `accessible` grouping.
 * `accessible={true}` on a parent merges all descendant text into one VoiceOver/NVDA utterance;
 * keep this wrapper transparent so users move heading → link → text line by line.
 */
export const webNamedRegion = (labelledByHeadingNativeId: string) =>
  Platform.OS === 'web'
    ? ({
        accessible: false,
        _web: {
          role: 'region' as const,
          'aria-labelledby': labelledByHeadingNativeId,
        },
      } as const)
    : ({} as const);

/**
 * Web: put each section title in the tab order so keyboard users can move from block to block
 * (address → ways to order → each hours group) without relying only on the rotor.
 */
export const webSectionHeadingWebProps = (ariaLevel: 2 | 3) =>
  Platform.OS === 'web'
    ? ({
        tabIndex: 0 as const,
        _web: {
          role: 'heading' as const,
          'aria-level': ariaLevel,
        },
      } as const)
    : ({} as const);

/**
 * Web: non-links need an explicit tab stop so keyboard users can move through section body copy.
 * `accessibilityLabel` supplies the intended spoken text for that focused copy.
 *
 * `role="group"` is required because `aria-label` is prohibited on elements with no role
 * (generic `<div>`), per WAI-ARIA. The group role gives the element a valid host for the label
 * without introducing landmark semantics.
 */
export const webFocusableSectionTextProps = (accessibilityLabel: string) =>
  Platform.OS === 'web'
    ? ({
        tabIndex: 0 as const,
        accessible: true,
        accessibilityLabel,
        _web: {
          role: 'group' as const,
          'aria-label': accessibilityLabel,
        },
      } as const)
    : ({} as const);
